import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { addSupportTicketAttachment } from "../apis/addSupportTicketAttachment";
import { createSupportTicket } from "../apis/createSupportTicket";
import type { CreateSupportTicketFormData } from "../supportTicketTypes";
import { uploadSupportTicketAttachmentToStorage } from "../utils/uploadSupportTicketAttachment";

/**
 * Options for {@link useCreateSupportTicket}.
 */
export interface UseCreateSupportTicketOptions {
  /** Called after a ticket is created successfully. */
  onSuccess?: (ticketId: string) => void;
}

/**
 * Return value for {@link useCreateSupportTicket}.
 */
export interface UseCreateSupportTicketReturn {
  /** Submits the create-ticket form to the API. Returns true on success. */
  createTicket: (form: CreateSupportTicketFormData) => Promise<boolean>;
  /** Whether the create-ticket request is in flight. */
  isCreating: boolean;
}

/**
 * Handles create-ticket submission with toast feedback for the support tickets page.
 *
 * @param options - Optional success callback for refreshing local ticket state.
 * @returns Async create handler and submitting flag for the modal.
 */
export function useCreateSupportTicket(
  options: UseCreateSupportTicketOptions = {},
): UseCreateSupportTicketReturn {
  const { onSuccess } = options;
  const [isCreating, setIsCreating] = useState(false);

  const createTicket = useCallback(
    async (form: CreateSupportTicketFormData): Promise<boolean> => {
      const orgId = getOrgIdForFetchExternalWorklist();
      if (!orgId) {
        toast.error("Organization id is unavailable.");
        return false;
      }

      setIsCreating(true);

      try {
        const response = await createSupportTicket(orgId, form);

        // Ticket-scoped attachment (not tied to a comment): upload to Firebase,
        // then register against the new ticket. A failure here must not undo the
        // already-created ticket — surface a warning and continue.
        if (form.attachment) {
          try {
            const uploaded = await uploadSupportTicketAttachmentToStorage(
              form.attachment,
              orgId,
              response.id,
            );
            await addSupportTicketAttachment({
              org_id: orgId,
              ticket_id: response.id,
              filename: uploaded.filename,
              gcs_path: uploaded.gcs_path,
              content_type: uploaded.content_type,
              size: uploaded.size,
            });
          } catch (attachmentError) {
            logError(
              attachmentError as Error,
              "Error uploading create-ticket attachment",
            );
            toast.error("Ticket created, but the attachment upload failed.");
          }
        }

        // Fire the success toast last so it appears as the modal closes, kept
        // in sync even when an attachment upload adds latency.
        toast.success("Support ticket created successfully.");
        onSuccess?.(response.id);
        return true;
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to create support ticket.",
        );
        return false;
      } finally {
        setIsCreating(false);
      }
    },
    [onSuccess],
  );

  return {
    createTicket,
    isCreating,
  };
}
